import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Trash2, Send } from 'lucide-react';
import AudioRecordingHint from './AudioRecordingHint';

interface AudioRecorderProps {
  onSendAudio: (blob: Blob) => void;
  onRecordingStateChange: (isRecording: boolean) => void;
  disabled?: boolean;
}

const MIN_AUDIO_SECONDS = 1;
const CANCEL_DISTANCE = 150;
const HOLD_TO_RECORD_MS = 450;
const CANCEL_CLICK_SUPPRESSION_MS = 650;

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onSendAudio, onRecordingStateChange, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isLockedMode, setIsLockedMode] = useState(false);
  const [duration, setDuration] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isCanceling, setIsCanceling] = useState(false);
  const [trashScale, setTrashScale] = useState(1);
  const [isStarting, setIsStarting] = useState(false);
  const [isGesturePending, setIsGesturePending] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const startXRef = useRef<number>(0);
  const activePointerIdRef = useRef<number | null>(null);
  const pointerIsDownRef = useRef(false);
  const releaseQueuedRef = useRef(false);
  const isLockedModeRef = useRef(false);
  const holdModeActivatedRef = useRef(false);
  const stopInProgressRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  const suppressClickUntilRef = useRef(0);
  const ignoreClickUntilRef = useRef(0);
  const isStartingRef = useRef(false);
  const holdIntentTimeoutRef = useRef<number | null>(null);
  const startRequestIdRef = useRef(0);

  const mimeTypeRef = useRef<string>('');

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const clearTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const clearHoldIntentTimer = () => {
    if (holdIntentTimeoutRef.current) {
      window.clearTimeout(holdIntentTimeoutRef.current);
      holdIntentTimeoutRef.current = null;
    }
  };

  const stopTracks = () => {
    const stream = mediaRecorderRef.current?.stream;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const suppressUpcomingClick = useCallback((durationMs = 0) => {
    suppressNextClickRef.current = true;
    if (durationMs > 0) {
      suppressClickUntilRef.current = Date.now() + durationMs;
    }
  }, []);

  const shouldSuppressClick = useCallback(() => {
    if (suppressNextClickRef.current || Date.now() < suppressClickUntilRef.current) {
      suppressNextClickRef.current = false;
      return true;
    }

    return false;
  }, []);

  const getRecordedSeconds = useCallback(() => {
    if (!startTimeRef.current) return 0;
    return (Date.now() - startTimeRef.current) / 1000;
  }, []);

  const lockRecording = useCallback(() => {
    isLockedModeRef.current = true;
    setIsLockedMode(true);
    setIsGesturePending(false);
    setTranslateX(0);
    setIsCanceling(false);
    setTrashScale(1);
  }, []);

  const cleanup = useCallback(() => {
    startRequestIdRef.current += 1;
    setIsRecording(false);
    onRecordingStateChange(false);
    setIsLockedMode(false);
    setTranslateX(0);
    setIsCanceling(false);
    setTrashScale(1);
    setDuration(0);
    setIsStarting(false);
    setIsGesturePending(false);

    activePointerIdRef.current = null;
    pointerIsDownRef.current = false;
    releaseQueuedRef.current = false;
    isLockedModeRef.current = false;
    holdModeActivatedRef.current = false;
    stopInProgressRef.current = false;
    suppressNextClickRef.current = false;
    ignoreClickUntilRef.current = 0;
    isStartingRef.current = false;

    clearTimer();
    clearHoldIntentTimer();
    mediaRecorderRef.current = null;
  }, [onRecordingStateChange]);

  const startRecording = async () => {
    if (disabled || isStartingRef.current || mediaRecorderRef.current) return false;

    const startRequestId = startRequestIdRef.current + 1;
    startRequestIdRef.current = startRequestId;
    isStartingRef.current = true;
    setIsStarting(true);
    onRecordingStateChange(true);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Error: Tu navegador o el entorno bloquea el acceso al microfono por falta de contexto seguro (HTTPS) o soporte.');
        cleanup();
        return false;
      }

      if (typeof MediaRecorder === 'undefined') {
        alert('Error: Tu navegador no soporta la grabacion de audio.');
        cleanup();
        return false;
      }

      let mimeType = 'audio/webm';
      if (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        }
      }
      mimeTypeRef.current = mimeType;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      if (startRequestIdRef.current !== startRequestId) {
        stream.getTracks().forEach(track => track.stop());
        return false;
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();

      setIsRecording(true);
      onRecordingStateChange(true);
      setIsLockedMode(isLockedModeRef.current);
      setDuration(0);
      setTranslateX(0);
      setIsCanceling(false);
      setTrashScale(1);

      stopInProgressRef.current = false;
      startTimeRef.current = Date.now();

      clearTimer();
      timerIntervalRef.current = window.setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 100);

      return true;
    } catch (err: any) {
      console.error('Error al iniciar grabacion:', err);

      let userMessage = 'No se pudo iniciar la grabacion.';

      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        userMessage = 'No se detecto ningun microfono. Asegurate de que este conectado y habilitado en la configuracion de Windows.';
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        userMessage = 'Permiso denegado. Permite el acceso al microfono en los ajustes de tu navegador.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        userMessage = 'El microfono esta en uso por otra aplicacion o hay un error de hardware.';
      } else if (err.message) {
        userMessage = `No se pudo iniciar la grabacion: ${err.message}`;
      }

      alert(userMessage);
      cleanup();
      return false;
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  };

  const stopRecording = useCallback((shouldSend: boolean) => {
    if (stopInProgressRef.current) return;

    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      cleanup();
      return;
    }

    stopInProgressRef.current = true;

    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
      const recordedSeconds = getRecordedSeconds();

      if (shouldSend && recordedSeconds >= MIN_AUDIO_SECONDS && audioBlob.size > 0) {
        onSendAudio(audioBlob);
      }

      cleanup();
    };

    mediaRecorderRef.current.stop();
    stopTracks();
  }, [cleanup, getRecordedSeconds, onSendAudio]);

  const cancelRecording = useCallback(() => {
    suppressUpcomingClick(CANCEL_CLICK_SUPPRESSION_MS);
    setIsCanceling(true);
    stopRecording(false);
  }, [stopRecording, suppressUpcomingClick]);

  const activateHoldMode = useCallback(() => {
    if (!pointerIsDownRef.current || stopInProgressRef.current) return;

    holdModeActivatedRef.current = true;
    isLockedModeRef.current = false;
    setIsLockedMode(false);
    setIsGesturePending(false);
    setTranslateX(0);
    setTrashScale(1);
  }, []);

  const handlePointerDown = async (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || isStartingRef.current) return;

    suppressUpcomingClick();
    ignoreClickUntilRef.current = Date.now() + 800;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    activePointerIdRef.current = e.pointerId;
    pointerIsDownRef.current = true;
    releaseQueuedRef.current = false;
    holdModeActivatedRef.current = false;
    startXRef.current = e.clientX;

    if (isRecording && isLockedModeRef.current) return;

    setIsGesturePending(true);
    clearHoldIntentTimer();
    holdIntentTimeoutRef.current = window.setTimeout(activateHoldMode, HOLD_TO_RECORD_MS);

    const started = await startRecording();
    if (!started) return;

    if (releaseQueuedRef.current || !pointerIsDownRef.current) {
      lockRecording();
    }
  };

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!isRecording || !pointerIsDownRef.current || stopInProgressRef.current) return;
    if (isLockedModeRef.current) return;
    if (!holdModeActivatedRef.current) return;
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;

    const delta = e.clientX - startXRef.current;

    if (delta < 0) {
      setTranslateX(delta);

      const progress = Math.min(Math.abs(delta) / CANCEL_DISTANCE, 1);
      setTrashScale(1 + (progress * 0.3));

      if (delta < -CANCEL_DISTANCE) {
        cancelRecording();
      }
    } else {
      setTranslateX(0);
      setTrashScale(1);
    }
  }, [cancelRecording, isRecording]);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    if (activePointerIdRef.current === null && !pointerIsDownRef.current) return;
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;

    clearHoldIntentTimer();
    activePointerIdRef.current = null;
    pointerIsDownRef.current = false;
    setTranslateX(0);
    setTrashScale(1);

    if (stopInProgressRef.current) return;

    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state !== 'recording') {
      releaseQueuedRef.current = true;
      lockRecording();
      return;
    }

    if (isLockedModeRef.current) return;

    if (!holdModeActivatedRef.current) {
      lockRecording();
      return;
    }

    const recordedSeconds = getRecordedSeconds();
    if (recordedSeconds >= MIN_AUDIO_SECONDS) {
      stopRecording(true);
    } else {
      cancelRecording();
    }
  }, [cancelRecording, getRecordedSeconds, lockRecording, stopRecording]);

  const handleSendLockedRecording = () => {
    if (stopInProgressRef.current || getRecordedSeconds() < MIN_AUDIO_SECONDS) return;
    stopRecording(true);
  };

  const handleCancelLockedRecording = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    cancelRecording();
  };

  const blockCancelButtonPropagation = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMicClick = async () => {
    if (shouldSuppressClick()) return;

    if (disabled || isStartingRef.current || stopInProgressRef.current) return;

    if (isRecording && isLockedModeRef.current) {
      handleSendLockedRecording();
      return;
    }

    isLockedModeRef.current = true;
    const started = await startRecording();
    if (started) {
      lockRecording();
    }
  };

  const handleSendLockedRecordingPointer = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    ignoreClickUntilRef.current = Date.now() + 800;
    handleSendLockedRecording();
  };

  const handleSendLockedRecordingClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (Date.now() < ignoreClickUntilRef.current) return;
    handleSendLockedRecording();
  };

  const handleCancelLockedRecordingClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (Date.now() < ignoreClickUntilRef.current) return;
    cancelRecording();
  };

  useEffect(() => {
    if (isRecording || isStarting) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isRecording, isStarting, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    return () => {
      startRequestIdRef.current += 1;
      clearTimer();
      clearHoldIntentTimer();
      stopTracks();
    };
  }, []);

  const isDragging = Math.abs(translateX) > 10;
  const canSendLockedAudio = duration >= MIN_AUDIO_SECONDS;
  const showRecordingControls = isRecording || isStarting || isGesturePending;
  const isActiveMicVisual = isRecording || isStarting || isGesturePending;
  const isHoldMode = showRecordingControls && !isLockedMode && !isGesturePending;
  const isPreparing = isGesturePending || (isStarting && !isRecording);

  return (
    <>
      {showRecordingControls && (
        <div className="absolute inset-0 bg-panel z-40 flex items-center justify-between px-2 rounded-2xl motion-fade-through-fast shadow-sm pl-2 overflow-hidden border border-danger pointer-events-none">
          <div className={`${!isRecording ? (isLockedMode ? 'w-14' : 'w-0') : isLockedMode ? 'w-36' : 'w-24'} shrink-0 flex items-center h-full relative pointer-events-none`}>
            <div
              className={`flex items-center gap-2 absolute left-0 transition-all duration-300 ${isDragging && isHoldMode ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0'}`}
            >
              {isLockedMode && (
                <button
                  type="button"
                  onPointerDown={blockCancelButtonPropagation}
                  onPointerUp={handleCancelLockedRecording}
                  onClick={handleCancelLockedRecordingClick}
                  className="group relative z-[60] w-14 h-14 flex flex-shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger pointer-events-auto touch-manipulation transition-colors hover:bg-danger-soft active:bg-danger-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-danger focus-visible:ring-offset-2"
                  aria-label="Cancelar audio"
                >
                  <Trash2 size={19} className="pointer-events-none" />
                </button>
              )}
              <div className={`${isRecording ? 'flex' : 'hidden'} items-center gap-[3px] h-6 pointer-events-none`}>
                <div className="voice-bar bg-danger-action" style={{ animationDelay: '0ms' }}></div>
                <div className="voice-bar bg-danger-action" style={{ animationDelay: '200ms' }}></div>
                <div className="voice-bar bg-danger-action" style={{ animationDelay: '400ms' }}></div>
                <div className="voice-bar bg-danger-action" style={{ animationDelay: '100ms' }}></div>
              </div>
              <span className={`${isRecording ? '' : 'hidden'} text-ink font-mono text-base font-medium min-w-[42px] pointer-events-none`}>
                {formatDuration(duration)}
              </span>
            </div>

            <div
              className={`absolute left-2 transition-all duration-300 ${isDragging && isHoldMode ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
              style={{
                transform: isDragging ? `scale(${trashScale})` : 'scale(0.5)'
              }}
            >
              <div className="p-2 rounded-full text-danger pointer-events-none">
                <Trash2 size={28} className={isCanceling ? 'animate-shake' : ''} />
              </div>
            </div>
          </div>

          <AudioRecordingHint isPreparing={isPreparing} isLockedMode={isLockedMode}
            holdProgress={isHoldMode ? Math.max(0, 1 - (Math.abs(translateX) / CANCEL_DISTANCE)) : 1}
            translateX={isHoldMode ? translateX : 0} />

          <div className={`${isHoldMode ? 'w-16' : 'w-14'} shrink-0`}></div>
        </div>
      )}

      <div
        className="relative z-50 flex items-center justify-center"
        style={{
          transform: isHoldMode ? `translateX(${translateX}px)` : undefined
        }}
      >
        {isActiveMicVisual && isHoldMode && (
          <div className="absolute w-full h-full bg-danger-action rounded-full animate-ripple -z-10"></div>
        )}

        {showRecordingControls && isLockedMode ? (
          <button
            type="button"
            onPointerUp={handleSendLockedRecordingPointer}
            onClick={handleSendLockedRecordingClick}
            aria-disabled={!canSendLockedAudio}
            className={`flex items-center justify-center rounded-full transition-all duration-300 shadow-md select-none touch-manipulation w-12 h-12 bg-action text-white shadow-lg ${canSendLockedAudio ? 'hover:bg-action-hover' : 'opacity-60'}`}
            aria-label={canSendLockedAudio ? 'Enviar audio' : 'Grabando audio'}
          >
            <Send size={20} className="pointer-events-none translate-x-[-1px] translate-y-[1px]" />
          </button>
        ) : (
          <button
            type="button"
            onPointerDown={handlePointerDown}
            onPointerUp={(e) => handlePointerUp(e.nativeEvent)}
            onPointerCancel={(e) => handlePointerUp(e.nativeEvent)}
            onClick={handleMicClick}
            disabled={disabled}
            className={`flex items-center justify-center rounded-full transition-all duration-300 shadow-md select-none touch-none ${isActiveMicVisual
              ? 'bg-danger-action text-white w-16 h-16 shadow-xl scale-110'
              : disabled
                ? 'bg-line text-white w-11 h-11 cursor-not-allowed'
                : 'bg-action text-white w-11 h-11 hover:bg-action-hover'
            }`}
            aria-label={isActiveMicVisual ? 'Grabando audio' : 'Iniciar grabacion de audio'}
          >
            <Mic
              size={isActiveMicVisual ? 32 : 22}
              className={`pointer-events-none transition-transform duration-200 ${isActiveMicVisual ? 'scale-110' : ''}`}
            />
          </button>
        )}
      </div>
    </>
  );
};

export default AudioRecorder;
